/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { useRequestGeolocation } from "@packages/sdk";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HallowDataTestId } from "@tools/playwright/global";

import type { ThemeType } from "../../../../global/emotion";
import { numericPercentages, spacing } from "../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import type { TextProps } from "../../_base/_core/Text/Text.emotion";
import { Ratings } from "../../marketing/Ratings";
import { Reviews } from "../../marketing/Reviews";
import type { StackProps } from "../layout/Stack/Stack.emotion";
import { Stack } from "../layout/Stack/Stack.emotion";
import { AppleAuthButton } from "./AppleAuthButton.emotion";
import { AuthLegal } from "./AuthLegal";
import type { AuthFlowProps } from "./AuthTypes.emotion";
import { GoogleAuthButton } from "./GoogleAuthButton.emotion";

const containerStyle: Interpolation = {
  padding: spacing.ml,
  width: numericPercentages[100],
};
const cssRatings: Interpolation<ThemeType> = (theme) => ({
  paddingTop: theme.spacing.ms,
  [cssMediaQueryMinWidthTablet]: {
    paddingTop: theme.spacing.l,
  },
});
const cssReviews: Interpolation<ThemeType> = (theme) => ({
  paddingTop: theme.spacing.ms,
  [cssMediaQueryMinWidthTablet]: {
    paddingTop: theme.spacing.l,
  },
});
const studentHrStyle: Interpolation<ThemeType> = (theme) => ({
  borderBottomStyle: "solid",
  borderBottomWidth: theme.stroke.light,
  borderBottomColor: theme.semanticColors.neutralsVeryLow,
  marginBlock: theme.spacing.m,
});

export type AuthMainFlowProps = AuthFlowProps & {
  isRatings?: boolean;
  isReviews?: boolean;
  legalCSS?: Interpolation;
  legalProps?: Partial<TextProps>;
  allowStudentLogin?: boolean;
  studentButtonLabelKey?: string;
} & Pick<StackProps, "className">;

export const AuthMainFlow = ({
  isRatings,
  isReviews,
  onAuth,
  setFlow,
  email,
  legalCSS = {},
  legalProps,
  allowedMethods,
  className,
  allowStudentLogin,
  studentButtonLabelKey = "launch_im_a_student",
  ...props
}: AuthMainFlowProps) => {
  const t = useTranslations();
  const { query: queryGeolocation } = useRequestGeolocation();
  const allowPhoneFlow =
    queryGeolocation.data?.country === "US" && allowedMethods.includes("phone");
  const initialSimpleFlow =
    queryGeolocation.data?.country === "US" && allowPhoneFlow && !email
      ? "phone"
      : "email";
  const { track } = useAnalytics();

  useViewedScreen({ screen: "auth_type_login_primer" });

  return (
    <GoogleOAuthProvider
      clientId={process.env.NEXT_PUBLIC_ACCESS_GOOGLE_CLIENT_ID}
    >
      <Stack gap={spacing.ms} css={containerStyle} className={className}>
        {!queryGeolocation.isLoading &&
          (allowPhoneFlow || allowedMethods.includes("email")) && (
            <Button
              isFullWidth
              size="l"
              onClick={() => {
                track({
                  event: HallowAnalyticsEvent.EmailButtonTapped,
                  properties: { screen_name: "auth_type_login_primer" },
                });
                setFlow(initialSimpleFlow);
              }}
              data-testid={HallowDataTestId.AuthContinueWithPhoneOrEmailButton}
            >
              {t(
                allowPhoneFlow
                  ? "sms_sign_up_continue_phone_or_email"
                  : "launch_continue_with_email",
              )}
            </Button>
          )}
        {allowedMethods.includes("google") && (
          <GoogleAuthButton
            onAuth={onAuth}
            screen="auth_type_login_primer"
            utm={props.utm}
          />
        )}
        {allowedMethods.includes("apple") && (
          <AppleAuthButton
            onAuth={onAuth}
            screen="auth_type_login_primer"
            utm={props.utm}
          />
        )}
        {allowStudentLogin ? (
          <>
            <div css={studentHrStyle} />
            <Button
              variant="outline"
              isFullWidth
              size="l"
              onClick={() => {
                track({
                  event: HallowAnalyticsEvent.TappedIAmAStudentLogin,
                  properties: { screen_name: "auth_type_login_primer" },
                });
                setFlow("student");
              }}
              data-testid={HallowDataTestId.AuthContinueAsStudentButton}
            >
              {t(studentButtonLabelKey)}
            </Button>
          </>
        ) : null}
        <AuthLegal legalCSS={legalCSS} legalProps={legalProps} />
        {isRatings && <Ratings css={cssRatings} />}
        {isReviews && <Reviews css={cssReviews} />}
      </Stack>
    </GoogleOAuthProvider>
  );
};

AuthMainFlow.displayName = "AuthMainFlow";
